// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Teeter — the three modes (principle #14). The RULE SET is the axis that changes the
// game, not a dialled number: Dash is a pure racing line (no hazards), Sinkhole adds
// pits that send you back to the start, Scatter turns it into a gem-collection route.
// Maze SIZE also changes how it plays and is MEASURED before ship (tests/balance).
//
// The host's chosen mode travels FROZEN inside the round start (rematch roundOpts);
// guests render state().hostOpts. An id off the wire is always modeOf()-validated so
// an unknown value falls back to Dash rather than reaching the generator as undefined
// (guarded with Object.hasOwn — a wire key must never resolve a prototype member).

export type ModeId = 'dash' | 'sinkhole' | 'scatter';

export interface Mode {
  readonly id: ModeId;
  readonly name: string;
  readonly blurb: string;
  /** Maze size in ROOMS (the grid is 2*cols+1 by 2*rows+1). */
  readonly cols: number;
  readonly rows: number;
  /** Number of pits placed OFF the safe route (0 = none). */
  readonly holes: number;
  /** Gems to collect before the exit opens (0 = go straight for the exit). */
  readonly gems: number;
  /** Fraction of dead-ends braided open into loops, 0..1 (route choice). */
  readonly braid: number;
  /** Round time limit in ms — miss it and you DNF. */
  readonly limitMs: number;
}

export const MODES: Record<ModeId, Mode> = {
  dash: {
    id: 'dash',
    name: 'Dash',
    blurb: 'A small, open maze. No pits, no gems — just the fastest line to the exit.',
    cols: 8,
    rows: 8,
    holes: 0,
    gems: 0,
    braid: 0.6,
    limitMs: 60_000,
  },
  sinkhole: {
    id: 'sinkhole',
    name: 'Sinkhole',
    blurb: 'A bigger maze studded with pits. Fall in and you roll back to the start.',
    cols: 12,
    rows: 12,
    holes: 12,
    gems: 0,
    braid: 0.4,
    limitMs: 90_000,
  },
  scatter: {
    id: 'scatter',
    name: 'Scatter',
    blurb: 'Grab all five gems, then find the exit — with pits in your way.',
    cols: 14,
    rows: 14,
    holes: 9,
    gems: 5,
    braid: 0.32,
    limitMs: 150_000,
  },
};

export const MODE_IDS: ModeId[] = ['dash', 'sinkhole', 'scatter'];

export const DEFAULT_MODE: Mode = MODES.dash;

/** Resolve a (possibly untrusted, off-the-wire) id to a Mode; unknown → Dash. */
export function modeOf(id: unknown): Mode {
  if (typeof id === 'string' && Object.hasOwn(MODES, id)) {
    return MODES[id as ModeId];
  }
  return DEFAULT_MODE;
}
